import { Box, Flex } from '@moonpig/launchpad-components'
import React, { FC } from 'react'
import { breakpointUp, styled } from '@moonpig/launchpad-utils'
import { Carousel } from '@moonpig/web-shared-components'
import { DepartmentsEnum } from '@moonpig/web-shared-types-graphql'
import { getParentDepartment } from '@moonpig/web-core-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import { system as s } from '@moonpig/launchpad-system'
import { useSearchStore } from '../../store/SearchStore'
import { DropDownMenu } from './DropDownMenu'
import {
  FilterCategory,
  IntegerFilter,
  FilterToggle,
} from '../../services/types/services'
import { QuickFilterToggle } from './QuickFilterToggle/QuickFilterToggle'
import { QuickSortBy } from './QuickSortBy/QuickSortBy'
import { DesktopQuickSortBy } from './QuickSortBy/DesktopQuickSortBy'
import { orderQuickFilters } from './orderQuickFilters'
import { FiltersMenuButton } from '../FiltersCarousel/components'
import { ClearButton } from '../ClearButton'
import { FilterSource } from '../../types'

const StyledQuickFiltersBar = styled(Flex)`
  flex-direction: row;
  justify-content: space-between;
  gap: ${spacingPx(6)};
  width: 100%;
  max-width: 1200px;

  ${breakpointUp('md')} {
    overflow-x: scroll;
    overscroll-behavior: contain;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  ${s({
    px: { md: 6, xl: 0 },
    marginX: 'auto',
  })}
`

const StyledCarousel = styled(Carousel)`
  width: 100%;
`

export const QuickFilters: FC<{ departments: DepartmentsEnum[] }> = ({
  departments,
}) => {
  const selectedFilters = useSearchStore(store => store.selectedFilters)
  const toggleFiltersMenu = useSearchStore(store => store.toggleFiltersMenu)
  const filters = useSearchStore(store => store.filters)
  const toggleFilter = useSearchStore(store => store.toggleFilter)
  const filtersMenuOpen = useSearchStore(store => store.filtersMenuOpen)
  const resultsLoading = useSearchStore(store => store.loading)
  const results = useSearchStore(store => store.results)
  const toggleFiltersCategory = useSearchStore(
    store => store.toggleFiltersCategory,
  )
  const applyIntegerFilter = useSearchStore(store => store.applyIntegerFilter)
  const onIntegerFilterChange = useSearchStore(
    store => store.onIntegerFilterChange,
  )
  const integerFilters = useSearchStore(store => store.integerFilters)
  const clearFilters = useSearchStore(store => store.clearFilters)

  const department = getParentDepartment(departments[0])
  const orderedFilters = orderQuickFilters(filters, department)

  return (
    <StyledQuickFiltersBar data-testid="dropdown-filters">
      {/* eslint-disable-next-line no-warning-comments */}
      {/* TODO: Add aria-label for carousel */}
      <StyledCarousel
        itemsGap={{ xs: 0 }}
        noSpacing
        alignItems="center"
        ariaLabel=""
      >
        <FiltersMenuButton
          toggleMenu={toggleFiltersMenu}
          isMenuOpen={filtersMenuOpen}
          appliedFilters={selectedFilters}
        />
        {(
          orderedFilters.filter(
            f =>
              f.__typename === 'FilterCategory' ||
              f.__typename === 'IntegerFilter',
          ) as (FilterCategory | IntegerFilter)[]
        ).map(filterCategory => {
          return (
            <DropDownMenu
              filterCategory={filterCategory}
              selectedFilters={selectedFilters}
              key={`${filterCategory.id}-quick-filter`}
              onChange={() => toggleFiltersCategory(filterCategory)}
              toggleFilter={toggleFilter}
              toggleFilterCategory={toggleFiltersCategory}
              onIntegerFilterChange={onIntegerFilterChange}
              integerFilters={integerFilters}
              applyIntegerFilter={applyIntegerFilter}
              count={results.totalCount}
              loading={resultsLoading}
              clearFilters={clearFilters}
            />
          )
        })}
        {(
          orderedFilters.filter(
            f =>
              f.__typename === 'FilterToggle' ||
              f.__typename === 'FilterInvertedToggle',
          ) as FilterToggle[]
        ).map(filter => {
          return (
            <QuickFilterToggle
              toggleFilter={filter}
              key={`${filter.id}-quick-filter-toggle`}
            />
          )
        })}
        <Box
          display={{ xs: 'flex', md: 'flex', lg: 'none' }}
          alignItems="center"
          data-testid="mobile-quick-sort-by"
        >
          <QuickSortBy onClick={() => toggleFiltersMenu('sort')} />
        </Box>
        <ClearButton
          level={0}
          source={FilterSource.QuickFilters}
          hide={selectedFilters.length === 0}
        />
      </StyledCarousel>
      <DesktopQuickSortBy onClick={() => toggleFiltersMenu('sort')} />
    </StyledQuickFiltersBar>
  )
}
